// ============================================================
// ReferenceLibrary.js — Real arrangement blueprints from
// well-known tracks. Each entry contains the actual section
// structure, bar counts, and energy curve so users can say
// "I want my track to feel like ___" and get a matching layout.
//
// This is NOT a streaming/playback system — it's a structural
// fingerprint database. Think of it as "arrangement DNA."
// ============================================================

import { SECTION_TYPES, generateArrangement, getTemplateForGenre } from './ArrangementGenerator.js';

const S = SECTION_TYPES;

// ============================================================
// THE LIBRARY
// Each reference track entry contains:
//   artist, title, genre, bpm, key,
//   sections: [{ type, label, bars, energy, notes }]
//   totalBars, style notes, and production insights
// ============================================================

export const REFERENCE_TRACKS = [

  // ============================================================
  // DRUM & BASS
  // ============================================================
  {
    id: 'subfocus-rock-it',
    artist: 'Sub Focus',
    title: 'Rock It',
    genre: 'Drum & Bass',
    subgenre: 'Dancefloor DnB',
    bpm: 174,
    key: 'F Minor',
    vibe: 'Peak-time dancefloor energy, vocal-driven buildup, massive drops',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 16, energy: 0.15, notes: 'Filtered breakbeat fading in, atmospheric pads. Sets the D&B tempo immediately.' },
      { type: S.BUILDUP,   label: 'Build 1',         bars: 16, energy: 0.5,  notes: 'Vocal hook introduced over rising synth line. Snare roll intensifies. Filter opens gradually.' },
      { type: S.DROP,       label: 'Drop 1',          bars: 16, energy: 1.0,  notes: 'Full energy — reese bass, chopped vocals, heavy break. The payoff. Everything firing.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 16, energy: 0.3,  notes: 'Kick drops out. Vocal melody carries it. Atmospheric reverb tails. Emotional contrast.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 8,  energy: 0.6,  notes: 'Faster this time — the audience knows what\'s coming. Bigger riser, vocal chops.' },
      { type: S.DROP,       label: 'Drop 2',          bars: 16, energy: 1.0,  notes: 'Second drop — variation on Drop 1. New bass pattern, extra percussion layer.' },
      { type: S.BREAK,      label: 'Mini Break',      bars: 8,  energy: 0.4,  notes: 'Brief pause — kick drops for 4 bars then re-enters. Keeps the momentum.' },
      { type: S.DROP,       label: 'Final Drop',      bars: 16, energy: 0.9,  notes: 'Last hurrah. Elements start peeling away in final 4 bars.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 16, energy: 0.1,  notes: 'Reverse of intro. Breakbeat filters down. DJ-friendly 16 bars to mix out.' },
    ],
    totalBars: 128,
    insights: [
      'The vocal hook is introduced in the build, not the drop — creates anticipation',
      'Drops use a reese bass that morphs across the stereo field',
      'Mini break between drops keeps energy without losing the crowd',
      'Breakdown is emotional — strip to just pads and vocals',
    ],
  },

  {
    id: 'chase-status-blind-faith',
    artist: 'Chase & Status',
    title: 'Blind Faith (ft. Liam Bailey)',
    genre: 'Drum & Bass',
    subgenre: 'Vocal DnB',
    bpm: 172,
    key: 'A Minor',
    vibe: 'Anthemic, vocal-driven, cinematic builds, festival energy',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 16, energy: 0.2,  notes: 'Piano chords with reverb. Establishes the emotional tone before any drums.' },
      { type: S.VERSE,     label: 'Verse 1',         bars: 16, energy: 0.4,  notes: 'Vocal enters over minimal beat. Half-time feel. Building tension slowly.' },
      { type: S.BUILDUP,   label: 'Build',           bars: 8,  energy: 0.65, notes: 'Snare roll accelerates. Vocal lifts. Everything pushes toward the drop.' },
      { type: S.DROP,       label: 'Drop 1',          bars: 16, energy: 1.0,  notes: 'Full D&B break with sub bass. Vocal fragments chopped and layered over the top.' },
      { type: S.VERSE,     label: 'Verse 2',         bars: 16, energy: 0.35, notes: 'Second verse — new lyrics, same minimal arrangement. Builds from verse 1.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 8,  energy: 0.7,  notes: 'Bigger build — stacked vocals, wider stereo, more percussion.' },
      { type: S.DROP,       label: 'Drop 2',          bars: 16, energy: 1.0,  notes: 'Same energy, new elements. Additional synth layer or vocal arrangement.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 16, energy: 0.25, notes: 'Just piano and vocal. The emotional peak. Let it breathe.' },
      { type: S.DROP,       label: 'Final Drop',      bars: 16, energy: 0.9,  notes: 'Final statement. Pull back in last 8 bars for the outro.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 16, energy: 0.1,  notes: 'Piano fades. Mirror the intro for closure.' },
    ],
    totalBars: 144,
    insights: [
      'Verse-chorus structure borrowed from pop, applied to D&B tempo',
      'The piano is the emotional anchor — it bookends the track',
      'Vocals are full in verses, chopped/processed in drops',
      'The breakdown after Drop 2 creates a false ending before the final payoff',
    ],
  },

  {
    id: 'netsky-memory-lane',
    artist: 'Netsky',
    title: 'Memory Lane',
    genre: 'Drum & Bass',
    subgenre: 'Liquid DnB',
    bpm: 174,
    key: 'D Major',
    vibe: 'Uplifting, melodic, liquid rollers, sunrise festival energy',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 16, energy: 0.2,  notes: 'Synth pad wash. Gentle. Sets the liquid tone.' },
      { type: S.BUILDUP,   label: 'Build',           bars: 16, energy: 0.5,  notes: 'Melodic lead enters. Light percussion builds. Arpeggiator pattern emerges.' },
      { type: S.DROP,       label: 'Drop 1',          bars: 16, energy: 0.85, notes: 'Liquid roller — rolling bassline, smooth breaks, the melody soars.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 16, energy: 0.3,  notes: 'Stripped to pads and a counter-melody. Ethereal and spacious.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 8,  energy: 0.55, notes: 'Subtle build — no big riser needed in liquid. Just layers returning.' },
      { type: S.DROP,       label: 'Drop 2',          bars: 16, energy: 0.9,  notes: 'Peak moment — additional harmony layer, wider bass, fuller mix.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 16, energy: 0.15, notes: 'Melody fades into reverb tails. Gentle comedown.' },
    ],
    totalBars: 104,
    insights: [
      'Liquid DnB drops are lower energy than dancefloor D&B — 0.85 not 1.0',
      'The melody is the star, not the bass — mix accordingly',
      'No dramatic risers — builds are about layering, not tension',
      'Rolling bassline should breathe and move, not pummel',
    ],
  },

  // ============================================================
  // BASS HOUSE
  // ============================================================
  {
    id: 'jauz-feel-the-volume',
    artist: 'Jauz',
    title: 'Feel The Volume',
    genre: 'Bass House',
    subgenre: 'Bass House',
    bpm: 128,
    key: 'F Minor',
    vibe: 'Grimy, heavy, festival bass house, speaker-shaking low end',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 16, energy: 0.15, notes: 'Four-on-the-floor kick with minimal hi-hats. Establishing groove.' },
      { type: S.BUILDUP,   label: 'Build 1',         bars: 16, energy: 0.5,  notes: 'Vocal sample introduced. Filter sweep up. Snare roll into drop.' },
      { type: S.DROP,       label: 'Drop 1',          bars: 16, energy: 1.0,  notes: 'Massive bass house drop. Distorted sub-bass, chopped vocal, relentless groove.' },
      { type: S.BREAK,      label: 'Break',           bars: 8,  energy: 0.4,  notes: 'Pull the bass. Ride on just kicks and vocal chops for 8 bars.' },
      { type: S.DROP,       label: 'Drop 1B',         bars: 16, energy: 0.95, notes: 'Same drop, slight variation. New hi-hat pattern or added perc.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 16, energy: 0.25, notes: 'Full strip. Atmospheric pad, maybe the vocal clean. Contrast.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 16, energy: 0.6,  notes: 'Longer build. More tension. Bigger payoff expected.' },
      { type: S.DROP,       label: 'Drop 2',          bars: 16, energy: 1.0,  notes: 'New bass sound or rhythm variation. Keep it fresh.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 16, energy: 0.1,  notes: 'Strip back to kick and hats. DJ mix-out friendly.' },
    ],
    totalBars: 136,
    insights: [
      'Bass house drops rely on distorted, gritty sub-bass — not clean sine waves',
      'The mini break between Drop 1 and 1B resets ears without losing momentum',
      'Breakdowns should be dramatic contrast — think silence after volume',
      'Four-on-the-floor kick never fully stops until the breakdown',
    ],
  },

  {
    id: 'chris-lake-turn-off-the-lights',
    artist: 'Chris Lake',
    title: 'Turn Off The Lights',
    genre: 'Bass House',
    subgenre: 'Tech House / Bass House',
    bpm: 126,
    key: 'G Minor',
    vibe: 'Dark, groovy, warehouse vibes, hypnotic and heavy',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 32, energy: 0.15, notes: 'Long DJ intro. Kick, hats, filtered bass tease. Hypnotic groove establishes.' },
      { type: S.BUILDUP,   label: 'Build',           bars: 8,  energy: 0.45, notes: 'Vocal hook. Subtle riser. Filter opens. Tension builds minimally.' },
      { type: S.DROP,       label: 'Drop 1',          bars: 16, energy: 0.9,  notes: 'Bassline revealed. Dark and groovy, not necessarily loud. The groove IS the drop.' },
      { type: S.BREAK,      label: 'Break',           bars: 8,  energy: 0.5,  notes: 'Pull bass, keep rhythm. Vocal loop carries it.' },
      { type: S.DROP,       label: 'Drop 1B',         bars: 16, energy: 0.9,  notes: 'Bassline returns with subtle variation — maybe an added acid line.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 16, energy: 0.2,  notes: 'Full strip to atmosphere. The room breathes. Build anticipation.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 8,  energy: 0.5,  notes: 'Same build pattern. The familiarity is intentional.' },
      { type: S.DROP,       label: 'Drop 2',          bars: 16, energy: 0.95, notes: 'Peak energy drop. Extra layer or harder bass. The climax.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 32, energy: 0.1,  notes: 'Long DJ outro. Mirror the intro. Filtered bass fade.' },
    ],
    totalBars: 152,
    insights: [
      'Chris Lake uses long DJ intros/outros (32 bars) — essential for club tracks',
      'The "drop" is groovy, not explosive. Bass house groove > bass house shock',
      'Repetition is a feature. Same vocal loop across sections builds hypnosis',
      'Darkness and space in the mix — don\'t overcrowd the arrangement',
    ],
  },

  // ============================================================
  // TECHNO
  // ============================================================
  {
    id: 'amelie-lens-hypnotized',
    artist: 'Amelie Lens',
    title: 'Hypnotized',
    genre: 'Techno',
    subgenre: 'Peak-Time Techno',
    bpm: 136,
    key: 'C Minor',
    vibe: 'Relentless, hypnotic, warehouse-shaking, minimal but powerful',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 32, energy: 0.15, notes: 'Kick pattern establishes. Hi-hats layer in. Pure techno groove — no melody needed.' },
      { type: S.BUILDUP,   label: 'Build 1',         bars: 16, energy: 0.4,  notes: 'Acid line fades in. Atmosphere darkens. Reverb swells.' },
      { type: S.DROP,       label: 'Main Section',    bars: 32, energy: 0.85, notes: 'Full groove. Techno doesn\'t "drop" like EDM — it arrives. All elements present, locked in.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 16, energy: 0.2,  notes: 'Kick drops. Atmosphere and reverb. Tension through absence.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 16, energy: 0.5,  notes: 'Elements re-layer. Kick returns subtly. Snare roll optional.' },
      { type: S.DROP,       label: 'Peak Section',    bars: 32, energy: 0.95, notes: 'Maximum intensity. New percussion or synth element added to original groove.' },
      { type: S.BREAKDOWN,  label: 'Break 2',         bars: 8,  energy: 0.3,  notes: 'Brief respite. Just enough to reset ears.' },
      { type: S.DROP,       label: 'Final Section',   bars: 16, energy: 0.8,  notes: 'Returning groove, elements slowly stripped away.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 32, energy: 0.1,  notes: 'Long DJ-friendly outro. Kick and hats only. 32 bars for mixing.' },
    ],
    totalBars: 200,
    insights: [
      'Techno is longer than other genres — 6-8 minutes is standard',
      'Long intros and outros (32 bars each) are non-negotiable for DJ sets',
      'The "drop" is a groove shift, not an explosion. Subtlety is power.',
      'Repetition IS the point — hypnosis through rhythm, not novelty',
    ],
  },

  // ============================================================
  // TRAP
  // ============================================================
  {
    id: 'rl-grime-core',
    artist: 'RL Grime',
    title: 'Core',
    genre: 'Trap',
    subgenre: 'Festival Trap',
    bpm: 140,
    key: 'E Minor',
    vibe: 'Anthemic, cinematic, massive builds, earth-shattering drops',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 8,  energy: 0.2,  notes: 'Cinematic atmosphere. Choir-like pads. Sets the epic tone.' },
      { type: S.BUILDUP,   label: 'Build 1',         bars: 16, energy: 0.6,  notes: 'Melodic lead builds. Snare roll accelerates. Filter sweep. Dramatic.' },
      { type: S.DROP,       label: 'Drop 1',          bars: 16, energy: 1.0,  notes: 'Massive. 808s, heavy brass stabs, half-time groove. Festival-level impact.' },
      { type: S.BREAK,      label: 'Break',           bars: 8,  energy: 0.35, notes: 'Stripped to atmosphere. Quick breather before the second wave.' },
      { type: S.DROP,       label: 'Drop 1B',         bars: 16, energy: 0.95, notes: 'Same drop structure, added elements. An extra horn or percussion layer.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 8,  energy: 0.2,  notes: 'Emotional moment. Pads and a counter-melody. Brief but impactful.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 8,  energy: 0.7,  notes: 'Short, explosive build. The crowd already knows what\'s coming.' },
      { type: S.DROP,       label: 'Drop 2',          bars: 16, energy: 1.0,  notes: 'New drop — different bass design or rhythm. The climax of the track.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 8,  energy: 0.1,  notes: 'Quick fade. Cinematic tail. Not DJ-functional.' },
    ],
    totalBars: 104,
    insights: [
      'Trap drops hit in half-time while the hi-hats stay fast — the contrast is the energy',
      '808 sub-bass is the foundation — it needs to be felt, not just heard',
      'Cinematic elements (orchestral hits, brass) elevate trap from beat to anthem',
      'The build is 50% of the experience — make it dramatic',
    ],
  },

  // ============================================================
  // POP / RADIO
  // ============================================================
  {
    id: 'dua-lipa-dont-start-now',
    artist: 'Dua Lipa',
    title: "Don't Start Now",
    genre: 'Pop',
    subgenre: 'Dance Pop',
    bpm: 124,
    key: 'B Minor',
    vibe: 'Disco-infused, confident, groovy, radio-ready hooks',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 4,  energy: 0.4,  notes: 'Vocal hook acapella. Immediately memorable. Hooks the listener in 4 bars.' },
      { type: S.VERSE,     label: 'Verse 1',         bars: 8,  energy: 0.5,  notes: 'Disco bass groove. Vocal melody established. Sparse arrangement.' },
      { type: S.PRE_CHORUS, label: 'Pre-Chorus',     bars: 4,  energy: 0.65, notes: 'Harmonic lift. Strings enter. Energy stepping up.' },
      { type: S.CHORUS,    label: 'Chorus 1',        bars: 8,  energy: 0.9,  notes: 'Full production. Bass, strings, percussion, vocal. The hook payoff.' },
      { type: S.VERSE,     label: 'Verse 2',         bars: 8,  energy: 0.5,  notes: 'New lyrics, same groove. Slight production additions over verse 1.' },
      { type: S.PRE_CHORUS, label: 'Pre-Chorus 2',   bars: 4,  energy: 0.7,  notes: 'Same lift, maybe a doubled vocal or new harmony.' },
      { type: S.CHORUS,    label: 'Chorus 2',        bars: 8,  energy: 0.95, notes: 'Bigger chorus. Added backing vocals or wider mix.' },
      { type: S.BRIDGE,    label: 'Bridge',          bars: 8,  energy: 0.4,  notes: 'New harmonic territory. Strip the production. Different vocal melody.' },
      { type: S.CHORUS,    label: 'Final Chorus',    bars: 8,  energy: 1.0,  notes: 'Biggest chorus. Everything firing. Vocal ad-libs.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 4,  energy: 0.3,  notes: 'Vocal hook returns, stripped back. Fades or hard cut.' },
    ],
    totalBars: 64,
    insights: [
      'Pop tracks are SHORT — 64 bars / 3 minutes. Every section earns its place.',
      'The acapella intro is a power move — immediate hook, no waiting',
      'Pre-chorus is essential in pop — it\'s the emotional ramp to the chorus',
      'The bridge provides harmonic contrast. Go somewhere unexpected.',
    ],
  },

  // ============================================================
  // HIP HOP
  // ============================================================
  {
    id: 'kanye-runaway',
    artist: 'Kanye West',
    title: 'Runaway',
    genre: 'Hip Hop',
    subgenre: 'Art Rap / Maximalist',
    bpm: 82,
    key: 'E Major',
    vibe: 'Cinematic, emotional, piano-driven, grand and vulnerable',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 16, energy: 0.15, notes: 'Solo piano. Single repeated note. One of the most iconic intros in hip hop.' },
      { type: S.VERSE,     label: 'Verse 1',         bars: 16, energy: 0.45, notes: 'Drums enter. Vocal delivery is understated. Piano remains the anchor.' },
      { type: S.CHORUS,    label: 'Chorus',          bars: 8,  energy: 0.7,  notes: 'Vocal melody. Pusha T feature. Layered backing vocals lift it.' },
      { type: S.VERSE,     label: 'Verse 2',         bars: 16, energy: 0.5,  notes: 'More intensity. String section enters. Production grows.' },
      { type: S.CHORUS,    label: 'Chorus 2',        bars: 8,  energy: 0.75, notes: 'Same hook, bigger production. More voices.' },
      { type: S.BRIDGE,    label: 'Bridge',          bars: 16, energy: 0.6,  notes: 'New progression. Vocoder/autotune solo. Experimental section.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 32, energy: 0.3,  notes: 'Extended vocoder outro. Dissolves into noise and atmosphere. Unconventional but powerful.' },
    ],
    totalBars: 112,
    insights: [
      'The piano motif carries the entire track — simplicity as strength',
      'Extended outro breaks all pop rules but creates emotional resonance',
      'Strings enter gradually across verses — each verse has more production',
      'Vulnerability in the vocal performance matches the sparse arrangement',
    ],
  },

  // ============================================================
  // ROCK / MELODIC HARDCORE
  // ============================================================
  {
    id: 'counterparts-bouquet',
    artist: 'Counterparts',
    title: 'Bouquet',
    genre: 'Melodic Hardcore',
    subgenre: 'Melodic Hardcore',
    bpm: 108,
    key: 'D Minor',
    vibe: 'Emotional, heavy, dynamic shifts between beauty and brutality',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 8,  energy: 0.3,  notes: 'Clean guitar arpeggios. Atmospheric. Sets the emotional tone.' },
      { type: S.VERSE,     label: 'Verse 1',         bars: 8,  energy: 0.7,  notes: 'Distortion kicks in. Screamed vocals over chugging guitars.' },
      { type: S.CHORUS,    label: 'Chorus',          bars: 8,  energy: 0.9,  notes: 'Melodic hook over heavy instrumentation. Clean vocals or gang vocals.' },
      { type: S.VERSE,     label: 'Verse 2',         bars: 8,  energy: 0.75, notes: 'Heavier riff variation. More aggressive vocal delivery.' },
      { type: S.CHORUS,    label: 'Chorus 2',        bars: 8,  energy: 0.95, notes: 'Bigger chorus. Layered guitars. Full band energy.' },
      { type: S.BRIDGE,    label: 'Bridge',          bars: 8,  energy: 0.4,  notes: 'Clean section. Vulnerable. Maybe just vocals and clean guitar.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 8,  energy: 1.0,  notes: 'Heaviest part. Down-tuned chug. Half-time feel. Crowd-killer.' },
      { type: S.CHORUS,    label: 'Final Chorus',    bars: 8,  energy: 0.85, notes: 'Last chorus, slightly pulled back. Bittersweet resolution.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 4,  energy: 0.2,  notes: 'Clean guitar returns. Feedback fades. Full circle.' },
    ],
    totalBars: 68,
    insights: [
      'Dynamic contrast is everything — whisper to scream, clean to distorted',
      'The breakdown is the heaviest section, placed late for maximum impact',
      'Bridge provides emotional vulnerability before the final punishment',
      'Clean guitar bookends (intro/outro) create narrative closure',
    ],
  },

  // ============================================================
  // DUBSTEP
  // ============================================================
  {
    id: 'skrillex-scary-monsters',
    artist: 'Skrillex',
    title: 'Scary Monsters And Nice Sprites',
    genre: 'Dubstep',
    subgenre: 'Brostep',
    bpm: 140,
    key: 'A Minor',
    vibe: 'Aggressive, chaotic, genre-defining wobble bass, maximum impact',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 8,  energy: 0.3,  notes: 'Eerie vocal sample. Pitched, processed. Builds curiosity.' },
      { type: S.BUILDUP,   label: 'Build 1',         bars: 8,  energy: 0.6,  notes: 'Vocal pitch rises. White noise sweep. Tension maxes out.' },
      { type: S.DROP,       label: 'Drop 1',          bars: 16, energy: 1.0,  notes: 'THE wobble bass. Half-time drums. Aggressive, distorted, in-your-face.' },
      { type: S.BREAK,      label: 'Break',           bars: 8,  energy: 0.2,  notes: 'Stripped to just the vocal sample again. Total contrast.' },
      { type: S.BUILDUP,   label: 'Build 2',         bars: 8,  energy: 0.65, notes: 'Same build, slightly different. Anticipation is key.' },
      { type: S.DROP,       label: 'Drop 2',          bars: 16, energy: 1.0,  notes: 'Different bass sound. New wobble pattern. Keeps the chaos fresh.' },
      { type: S.BREAKDOWN,  label: 'Breakdown',       bars: 16, energy: 0.15, notes: 'Beautiful section. Clean piano or synth. Calm before the final storm.' },
      { type: S.BUILDUP,   label: 'Build 3',         bars: 8,  energy: 0.7,  notes: 'Final build. Everything the listener has heard, compressed into 8 bars.' },
      { type: S.DROP,       label: 'Final Drop',      bars: 16, energy: 0.95, notes: 'Combination of both drops. Maximum chaos. Then sudden cut.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 4,  energy: 0.05, notes: 'Abrupt silence or a single vocal tail. Mic drop energy.' },
    ],
    totalBars: 108,
    insights: [
      'Each drop uses a DIFFERENT bass sound — variety is essential in dubstep',
      'The beautiful breakdown creates maximum contrast with the aggression',
      'Short intros and outros — dubstep is about impact, not DJ functionality',
      'Vocal processing (pitch, chop, reverse) carries the non-drop sections',
    ],
  },

  // ============================================================
  // HOUSE
  // ============================================================
  {
    id: 'disclosure-latch',
    artist: 'Disclosure',
    title: 'Latch (ft. Sam Smith)',
    genre: 'Deep House',
    subgenre: 'UK Garage / Deep House',
    bpm: 122,
    key: 'F# Minor',
    vibe: 'Soulful, warm, garage-influenced, Sam Smith\'s breakthrough vocal',
    sections: [
      { type: S.INTRO,     label: 'Intro',           bars: 8,  energy: 0.25, notes: 'Synth chord stabs. Establishes the UK garage shuffle.' },
      { type: S.VERSE,     label: 'Verse 1',         bars: 16, energy: 0.5,  notes: 'Vocal enters. Minimal production. The voice is the star.' },
      { type: S.CHORUS,    label: 'Chorus 1',        bars: 8,  energy: 0.85, notes: 'Full production. Bass drops in. Vocal hooks. Four-on-the-floor.' },
      { type: S.VERSE,     label: 'Verse 2',         bars: 16, energy: 0.5,  notes: 'Same pattern, new lyrics. Slight production additions.' },
      { type: S.CHORUS,    label: 'Chorus 2',        bars: 8,  energy: 0.9,  notes: 'Bigger. Additional synth arps. Wider stereo.' },
      { type: S.BRIDGE,    label: 'Bridge',          bars: 8,  energy: 0.35, notes: 'Stripped. Vocal and garage stabs only. Intimate moment.' },
      { type: S.CHORUS,    label: 'Final Chorus',    bars: 8,  energy: 0.95, notes: 'Everything. This is the peak. Vocal ad-libs layer.' },
      { type: S.OUTRO,      label: 'Outro',           bars: 8,  energy: 0.2,  notes: 'Synth stabs return solo. Garage shuffle fades.' },
    ],
    totalBars: 80,
    insights: [
      'Verse-chorus pop structure over a house foundation — crossover blueprint',
      'The vocal is mixed forward and dry compared to typical house',
      'Garage-style chord stabs define the production identity',
      'Bridge serves same function as in pop — harmonic/emotional detour',
    ],
  },

];


// ============================================================
// SEARCH / MATCHING FUNCTIONS
// ============================================================

/**
 * Find reference tracks that match the user's loop analysis.
 * Scores by genre match, BPM proximity, and key compatibility.
 */
export function findMatchingReferences(analysis, genreResult, options = {}) {
  const maxResults = options.maxResults || 5;
  const genreName = genreResult?.primary?.name || '';
  const userBpm = analysis.bpm;
  const userKey = analysis.key || '';

  const scored = REFERENCE_TRACKS.map(ref => {
    let score = 0;

    // Genre match (strongest signal)
    if (ref.genre === genreName) score += 50;
    else if (ref.subgenre === genreName) score += 40;
    // Genre family match
    else {
      const families = {
        'electronic': ['Drum & Bass', 'Liquid DnB', 'Neurofunk', 'Jungle', 'Bass House', 'Deep House', 'Tech House', 'Techno', 'Dubstep', 'Trance'],
        'hiphop': ['Hip Hop', 'Trap', 'Boom Bap', 'Drill', 'Lo-Fi Hip Hop'],
        'rock': ['Rock', 'Punk Rock', 'Metal', 'Melodic Hardcore'],
        'pop': ['Pop', 'R&B', 'Dance Pop'],
      };
      for (const [family, genres] of Object.entries(families)) {
        if (genres.includes(genreName) && genres.includes(ref.genre)) {
          score += 20;
          break;
        }
      }
    }

    // BPM proximity (within 10% = full points, further = less)
    const bpmDiff = Math.abs(userBpm - ref.bpm) / ref.bpm;
    if (bpmDiff < 0.02) score += 20;
    else if (bpmDiff < 0.05) score += 15;
    else if (bpmDiff < 0.10) score += 10;
    else if (bpmDiff < 0.20) score += 5;
    // Also check half/double time
    const halfDiff = Math.abs(userBpm - ref.bpm * 2) / (ref.bpm * 2);
    const doubleDiff = Math.abs(userBpm - ref.bpm / 2) / (ref.bpm / 2);
    if (halfDiff < 0.05 || doubleDiff < 0.05) score += 8;

    // Key compatibility (same key, relative major/minor, or fifth)
    if (userKey.includes(ref.key.split(' ')[0])) score += 10;

    return { ...ref, matchScore: score };
  });

  scored.sort((a, b) => b.matchScore - a.matchScore);
  return scored.slice(0, maxResults);
}

/**
 * Get all reference tracks for a specific genre.
 */
export function getReferencesForGenre(genreName) {
  return REFERENCE_TRACKS.filter(ref =>
    ref.genre === genreName || ref.subgenre === genreName
  );
}

/**
 * Get a specific reference track by ID.
 */
export function getReferenceById(id) {
  return REFERENCE_TRACKS.find(ref => ref.id === id);
}

/**
 * Generate an arrangement that mirrors a reference track's structure,
 * adapted to the user's loop analysis.
 */
export function generateReferenceArrangement(referenceId, analysis, roleResult) {
  const ref = getReferenceById(referenceId);
  if (!ref) throw new Error(`Reference track not found: ${referenceId}`);

  const loopRole = roleResult.role;
  const loopElements = roleResult.elements;

  const sections = ref.sections.map((section, index) => {
    // Determine if the user's loop goes here
    const isLoopSection = isLoopSectionForRole(section.type, loopRole);

    // Build element list for this section based on energy level
    const elements = inferElementsFromEnergy(section.energy, loopElements, section.type);

    return {
      type: section.type,
      label: section.label,
      bars: section.bars,
      startBar: ref.sections.slice(0, index).reduce((sum, s) => sum + s.bars, 0) + 1,
      energy: section.energy,
      guidance: section.notes,
      isLoopSection,
      elements,
      referenceNotes: section.notes,
    };
  });

  return {
    templateName: `${ref.artist} — "${ref.title}" Structure`,
    templateDescription: ref.vibe,
    genre: ref.genre,
    bpm: analysis.bpm,  // Use the user's BPM, not the reference's
    key: analysis.key,
    loopBars: analysis.bars,
    loopRole: roleResult.roleLabel,
    totalBars: ref.totalBars,
    totalDuration: Math.round((ref.totalBars * 4 * 60) / analysis.bpm),
    sections,
    referenceTrack: {
      artist: ref.artist,
      title: ref.title,
      originalBpm: ref.bpm,
      originalKey: ref.key,
      insights: ref.insights,
    },
  };
}

// --- Helpers ---

function isLoopSectionForRole(sectionType, loopRole) {
  const S = SECTION_TYPES;
  const dropTypes = [S.DROP, S.FIRST_DROP, S.SECOND_DROP, S.CHORUS];
  const breakdownTypes = [S.BREAKDOWN, S.BREAK];
  const buildTypes = [S.BUILDUP, S.PRE_CHORUS];
  const introTypes = [S.INTRO];

  switch (loopRole) {
    case 'main-drop':
    case 'full-mix':
    case 'bass-loop':
    case 'drum-break':
      return dropTypes.includes(sectionType);
    case 'breakdown':
    case 'pad-atmosphere':
      return breakdownTypes.includes(sectionType);
    case 'buildup':
      return buildTypes.includes(sectionType);
    case 'melodic-hook':
      return dropTypes.includes(sectionType) || breakdownTypes.includes(sectionType);
    default:
      return dropTypes.includes(sectionType);
  }
}

function inferElementsFromEnergy(energy, loopElements, sectionType) {
  const allElements = loopElements || [];
  if (energy >= 0.8) return allElements;
  if (energy >= 0.6) return allElements.filter(e => !['hi-hats', 'fx', 'vocals'].includes(e));
  if (energy >= 0.4) return allElements.filter(e => ['kick', 'bass', 'sub-bass', 'lead-synth'].includes(e));
  if (energy >= 0.2) return allElements.filter(e => ['bass', 'sub-bass', 'pads'].includes(e));
  return allElements.filter(e => ['pads', 'atmosphere'].includes(e));
}

/**
 * Get all unique genres in the library.
 */
export function getAvailableGenres() {
  const genres = new Set(REFERENCE_TRACKS.map(r => r.genre));
  return [...genres];
}

/**
 * Get all tracks as a searchable list.
 */
export function getAllReferences() {
  return REFERENCE_TRACKS.map(({ id, artist, title, genre, subgenre, bpm, key, vibe, totalBars }) => ({
    id, artist, title, genre, subgenre, bpm, key, vibe, totalBars
  }));
}

// ============================================================
// ITUNES GENRE → arrangementLab GENRE MAPPING
// Maps Apple Music / iTunes genre names to our internal genre system
// so we can generate arrangements from any searched track.
// ============================================================

const ITUNES_GENRE_MAP = {
  // Electronic
  'Dance': 'Bass House',
  'Electronic': 'Bass House',
  'House': 'Bass House',
  'Techno': 'Techno',
  'Trance': 'Trance',
  'Drum & Bass': 'Drum & Bass',
  'Jungle/Drum\'n\'bass': 'Drum & Bass',
  'Dubstep': 'Dubstep',
  'Electronica': 'Melodic Techno',
  'Ambient': 'Melodic Techno',
  'Downtempo': 'Lo-Fi Hip Hop',
  'IDM/Experimental': 'Melodic Techno',
  'Industrial': 'Techno',
  'Hardcore': 'Hardstyle',
  // Hip-Hop
  'Hip-Hop/Rap': 'Trap',
  'Hip Hop/Rap': 'Trap',
  'Hip-Hop': 'Trap',
  'Rap': 'Trap',
  'Trap': 'Trap',
  // R&B / Soul
  'R&B/Soul': 'R&B',
  'R&B': 'R&B',
  'Soul': 'R&B',
  'Neo-Soul': 'R&B',
  // Pop
  'Pop': 'Pop',
  'Singer/Songwriter': 'Pop',
  'Vocal': 'Pop',
  'Adult Contemporary': 'Pop',
  'K-Pop': 'Pop',
  'J-Pop': 'Pop',
  'Latin': 'Pop',
  'Reggaeton': 'Pop',
  // Rock
  'Rock': 'Rock',
  'Alternative': 'Rock',
  'Indie Rock': 'Rock',
  'Punk': 'Punk Rock',
  'Metal': 'Metal',
  'Hard Rock': 'Rock',
  'Grunge': 'Rock',
  'Progressive Rock': 'Rock',
  'Emo': 'Melodic Hardcore',
  'Hardcore': 'Melodic Hardcore',
  'Metalcore': 'Melodic Hardcore',
  'Post-Hardcore': 'Melodic Hardcore',
  // Other
  'Country': 'Pop',
  'Folk': 'Pop',
  'Reggae': 'Pop',
  'Jazz': 'R&B',
  'Blues': 'R&B',
  'Classical': 'Pop',
  'Soundtrack': 'Melodic Techno',
  'World': 'Pop',
  'Anime': 'Pop',
  'Children\'s Music': 'Pop',
  'Holiday': 'Pop',
  'Christian & Gospel': 'Pop',
  'Fitness & Workout': 'Bass House',
};

/**
 * Map an iTunes/Apple Music genre to our internal genre system.
 */
export function mapITunesGenre(itunesGenre) {
  // Guard against non-string payloads from the iTunes proxy (number/array/object)
  // — calling .toLowerCase() on those would crash arrangement generation.
  if (typeof itunesGenre !== 'string' || !itunesGenre) return 'Pop';
  // Direct match
  if (ITUNES_GENRE_MAP[itunesGenre]) return ITUNES_GENRE_MAP[itunesGenre];
  // Partial match
  const lower = itunesGenre.toLowerCase();
  for (const [key, val] of Object.entries(ITUNES_GENRE_MAP)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) return val;
  }
  return 'Pop'; // Safe fallback
}

/**
 * Search our curated library for a matching track by artist + title.
 * Returns the curated entry if found, null otherwise.
 */
export function findCuratedMatch(artist, title) {
  if (!artist || !title) return null;
  const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const a = normalize(artist);
  const t = normalize(title);
  return REFERENCE_TRACKS.find(ref => {
    const refA = normalize(ref.artist);
    const refT = normalize(ref.title);
    return (refA === a && refT === t) ||
           (a.includes(refA) && t.includes(refT)) ||
           (refA.includes(a) && refT.includes(t));
  }) || null;
}

/**
 * Generate an arrangement from search result metadata.
 * If we have a curated match, uses exact structure. Otherwise generates
 * a genre-appropriate arrangement using our templates.
 *
 * @param {Object} searchResult - from iTunes API proxy
 * @param {Object} analysis - from AudioAnalyzer
 * @param {Object} roleResult - from LoopRoleInference
 * @returns {Object} arrangement ready for canvas
 */
export function generateArrangementFromSearch(searchResult, analysis, roleResult) {
  // Check curated library first
  const curated = findCuratedMatch(searchResult.artist, searchResult.title);
  if (curated) {
    return generateReferenceArrangement(curated.id, analysis, roleResult);
  }

  // No curated match — generate from genre template
  const mappedGenre = mapITunesGenre(searchResult.genre);
  const durationSec = searchResult.durationMs ? searchResult.durationMs / 1000 : null;

  // Import arrangement generator to create genre-based arrangement
  // We return a structure that matches the reference arrangement format
  return {
    genre: mappedGenre,
    searchedTrack: {
      artist: searchResult.artist,
      title: searchResult.title,
      genre: searchResult.genre,
      artwork: searchResult.artwork,
      year: searchResult.year,
    },
    fromSearch: true,
    // The actual section generation will be done by ArrangementGenerator
    // via the existing generateArrangement() in Studio.jsx
    // We pass the mapped genre so Studio.jsx can call it with the right genre
    mappedGenre,
    durationSec,
  };
}

// ============================================================
// DEFAULT BPM BY GENRE (used when no BPM data available)
// ============================================================
const DEFAULT_BPM = {
  'Drum & Bass': 174, 'Liquid DnB': 174, 'Neurofunk': 174, 'Jungle': 170,
  'Bass House': 128, 'Deep House': 122, 'Tech House': 126, 'House': 124,
  'Techno': 130, 'Melodic Techno': 124, 'Trance': 138,
  'Dubstep': 140, 'Riddim': 150, 'Hardstyle': 150,
  'Trap': 140, 'Hip Hop': 90, 'Boom Bap': 90, 'Lo-Fi Hip Hop': 85, 'Drill': 140,
  'Pop': 120, 'R&B': 100, 'Dance Pop': 120,
  'Rock': 120, 'Punk Rock': 160, 'Metal': 130, 'Melodic Hardcore': 110,
};

/**
 * Generate a full arrangement analysis for a searched track WITHOUT
 * requiring user loop analysis. Used by the standalone "Analyze a Track" feature.
 *
 * For curated matches → returns exact section structure with producer insights.
 * For non-curated → generates genre-appropriate structure scaled to actual track duration.
 *
 * @param {Object} searchResult - from iTunes API { artist, title, genre, durationMs, artwork, year }
 * @returns {Object} Full arrangement with sections, ready for canvas rendering
 */
export function generateStandaloneTrackAnalysis(searchResult) {
  const curated = findCuratedMatch(searchResult.artist, searchResult.title);
  const mappedGenre = mapITunesGenre(searchResult.genre);
  const trackDurationSec = searchResult.durationMs ? searchResult.durationMs / 1000 : 210;
  const estimatedBpm = DEFAULT_BPM[mappedGenre] || 120;

  if (curated) {
    // Curated match — use the exact structural fingerprint
    const sections = curated.sections.map((section, index) => {
      const durationSec = (section.bars * 4 * 60) / curated.bpm;
      return {
        type: section.type,
        label: section.label,
        bars: section.bars,
        startBar: curated.sections.slice(0, index).reduce((sum, s) => sum + s.bars, 0) + 1,
        energy: section.energy,
        guidance: section.notes,
        isLoopSection: false,
        elements: inferElementsFromEnergy(section.energy, ['kick', 'snare', 'bass', 'synth', 'pads', 'hi-hats'], section.type),
        durationSec: Math.round(durationSec),
      };
    });

    return {
      templateName: `${curated.artist} — "${curated.title}" Structure`,
      templateDescription: curated.vibe,
      genre: curated.genre,
      bpm: curated.bpm,
      key: curated.key,
      totalBars: curated.totalBars,
      totalDuration: Math.round((curated.totalBars * 4 * 60) / curated.bpm),
      sections,
      referenceTrack: {
        artist: curated.artist,
        title: curated.title,
        originalBpm: curated.bpm,
        originalKey: curated.key,
        insights: curated.insights,
      },
      searchedTrack: {
        artist: searchResult.artist,
        title: searchResult.title,
        artwork: searchResult.artwork,
        genre: searchResult.genre,
        year: searchResult.year,
      },
      curatedMatch: true,
      standalone: true,
    };
  }

  // Non-curated — generate genre-appropriate structure scaled to track duration
  // Create synthetic analysis and role data for the arrangement generator
  const syntheticAnalysis = {
    bpm: estimatedBpm,
    key: 'C Minor',
    bars: 8,
    duration: trackDurationSec,
  };
  const syntheticRole = {
    role: 'main-drop',
    roleLabel: 'Main Section',
    elements: ['kick', 'snare', 'bass', 'synth', 'hi-hats'],
  };

  const arrangement = generateArrangement(syntheticAnalysis, { primary: { name: mappedGenre } }, syntheticRole);

  // Scale section bars to match actual track duration
  const templateDuration = arrangement.totalDuration;
  if (templateDuration > 0 && trackDurationSec > 0) {
    const scale = trackDurationSec / templateDuration;
    // Only scale if the difference is significant (>20%)
    if (Math.abs(scale - 1) > 0.2) {
      let totalScaledBars = 0;
      arrangement.sections = arrangement.sections.map(s => {
        const scaledBars = Math.max(4, Math.round((s.bars * scale) / 4) * 4); // Round to nearest 4 bars
        totalScaledBars += scaledBars;
        return { ...s, bars: scaledBars };
      });
      arrangement.totalBars = totalScaledBars;
      arrangement.totalDuration = Math.round(trackDurationSec);
    }
  }

  // Recompute startBar positions after scaling
  let currentBar = 1;
  arrangement.sections = arrangement.sections.map(s => {
    const section = { ...s, startBar: currentBar, isLoopSection: false };
    currentBar += s.bars;
    return section;
  });

  // Replace "YOUR LOOP" guidance with structural analysis framing
  arrangement.sections = arrangement.sections.map(s => ({
    ...s,
    guidance: s.guidance
      ? s.guidance.replace(/YOUR LOOP[^.]*\./i, `This is likely the main hook section of "${searchResult.title}".`)
                 .replace(/YOUR LOOP/gi, 'the main hook')
      : s.guidance,
  }));

  arrangement.templateName = `${searchResult.artist} — "${searchResult.title}" (${mappedGenre} Analysis)`;
  arrangement.templateDescription = `Estimated structure based on ${mappedGenre} conventions, scaled to ${Math.floor(trackDurationSec / 60)}:${String(Math.round(trackDurationSec % 60)).padStart(2, '0')} runtime`;
  arrangement.searchedTrack = {
    artist: searchResult.artist,
    title: searchResult.title,
    artwork: searchResult.artwork,
    genre: searchResult.genre,
    year: searchResult.year,
  };
  arrangement.curatedMatch = false;
  arrangement.standalone = true;

  return arrangement;
}

// ============================================================
// RECREATION GUIDE — "How to make a track like this"
// Genre-aware production blueprint for standalone track analysis
// ============================================================

const GENRE_PRODUCTION_GUIDES = {
  'electronic': {
    soundDesign: [
      'Start with your kick drum — layer a punchy transient sample with a sub-heavy body. Tune the sub to your track key.',
      'Design your bass using a wavetable synth (Serum, Vital). Start with a saw wave, add movement with LFO modulation on the filter cutoff.',
      'Build pads using detuned saw stacks or granular synthesis for atmospheric width. Automate filter sweeps for tension.',
      'Create risers with white noise + bandpass filter automation. Layer with pitch-rising synths for builds.',
    ],
    arrangement: [
      'Follow the energy curve — low energy intros build anticipation. The first drop should feel like a reward.',
      'Use subtractive arrangement: remove elements before a drop to make the impact bigger. Silence is a weapon.',
      'Breakdowns should contrast drops — if drops are dense and mono, breakdowns should be wide and sparse.',
      'Keep intro and outro DJ-friendly (16-32 bars of minimal elements) so the track mixes well in a set.',
    ],
    mixTechniques: [
      'Sidechain compress everything to the kick for that pumping feel. Use a ghost kick if your pattern is sparse.',
      'Keep sub bass in mono below 150Hz. Pan mid-frequency elements for width.',
      'Use parallel compression on drums for punch without killing dynamics.',
      'Reference on both monitors and earbuds — club systems and phones are your two audiences.',
    ],
    workflow: [
      'Start with an 8-bar loop: kick, bass, drums, and one melodic element.',
      'Build the drop section first — this is the emotional core of the track.',
      'Work backwards from the drop to create the build: strip elements, add risers and filter sweeps.',
      'Write the breakdown last — it should provide contrast and breathing room before the second drop.',
      'Arrangement, then mixdown, then master. Don\'t try to mix while you\'re still writing.',
    ],
  },
  'hip-hop': {
    soundDesign: [
      'The 808 is king — pick or design one that fits the vibe. Pitch it to the key. Saturate it for speaker translation.',
      'Layer your snare/clap: a crisp transient sample + a body layer. Apply light compression to glue them.',
      'Use textured hi-hats with subtle velocity variation and swing to create groove. Avoid robotic patterns.',
      'Sample or synthesize melodic loops — try pitching samples down for a dark vibe, or chop and rearrange.',
    ],
    arrangement: [
      'Verse sections should leave room for vocals — don\'t overload the mid-range with busy melodies.',
      'Hooks need to hit harder than verses. Add extra elements, louder drums, or a different melodic motif.',
      'Use 4-bar or 8-bar phrases. Most rap verses are 16 bars, hooks are 8 bars.',
      'Bridge or beat switch sections add replay value — even a 4-bar departure creates surprise.',
    ],
    mixTechniques: [
      'Leave 3-6dB headroom in the 300Hz-3kHz range for vocals. Your beat is a foundation, not the main event.',
      'Compress the 808 carefully — too much kills the sub, too little lacks punch.',
      'Use stereo widening on hi-hats and percs but keep kick and 808 in mono center.',
      'Soft-clip your master bus for loudness without harsh limiting.',
    ],
    workflow: [
      'Start with the melody or sample — the vibe drives everything in hip-hop production.',
      'Build your drum pattern around the melody. Let the kick and 808 lock in with the bass notes.',
      'Arrange the hook first, then strip elements for the verse.',
      'Add ear candy: vocal chops, transitions, reversed hits, one-shots on section changes.',
      'Leave space. Hip-hop breathes. The groove is in what you don\'t play as much as what you do.',
    ],
  },
  'pop': {
    soundDesign: [
      'Piano or guitar as your harmonic foundation — pick based on the vibe (piano = polished, guitar = organic).',
      'Use clean, bright synths for hooks. Supersaw stacks or pluck sounds work for modern pop.',
      'Drums should be punchy but not overwhelming. Layer acoustic and electronic drums for a hybrid feel.',
      'Add production ear candy: vinyl crackle, reversed reverb swells, pitch-shifted vocal chops.',
    ],
    arrangement: [
      'Verse → Pre-chorus → Chorus is the backbone. The pre-chorus is the secret weapon — it builds anticipation.',
      'Make your chorus undeniable. It should sound bigger, wider, and brighter than everything else.',
      'The bridge should take the listener somewhere unexpected — key change, tempo shift, or stripped instrumentation.',
      'Keep it under 3:30 for streaming. Get to the chorus by 0:45.',
    ],
    mixTechniques: [
      'Vocals are everything in pop. They should sit on top of the mix, not buried in it.',
      'Use bus compression to glue the instrumental together as one cohesive backing track.',
      'Automate volume throughout — rides make a pop mix feel alive and dynamic.',
      'Reference commercial releases in the same style. A/B constantly.',
    ],
    workflow: [
      'Write the chorus first — if the chorus doesn\'t hook you, the track won\'t either.',
      'Build the chord progression, then melody, then production layers.',
      'Record a rough vocal or topline early. The vocal shapes everything else.',
      'Strip things back for the verse — the chorus impact depends on contrast.',
      'Polish with automation: volume rides, filter sweeps, subtle delay throws.',
    ],
  },
  'rock': {
    soundDesign: [
      'Guitar tone is paramount. Experiment with amp sims, mic positions, and cabinet IRs until it feels right.',
      'Double-track rhythm guitars panned hard left and right for massive width.',
      'Bass should lock in with the kick drum. Tune and time them together.',
      'Use room mics or room reverb on drums for live energy — don\'t over-quantize.',
    ],
    arrangement: [
      'Verse → Chorus → Verse → Chorus → Bridge → Chorus is the classic rock formula. It works for a reason.',
      'The bridge should break the pattern — different chord progression, different feel, different dynamic.',
      'Intros should set the tone fast. A signature riff or drum hit in the first 4 bars.',
      'Outros can fade, crash to a stop, or resolve — match the energy of the song\'s message.',
    ],
    mixTechniques: [
      'Give the vocal its own frequency pocket. Cut 3kHz on guitars if the vocal is getting buried.',
      'Use parallel compression on the drum bus for power without squashing transients.',
      'Bass guitar needs both a DI and an amp track. Blend for clarity + grit.',
      'Don\'t over-compress. Rock music needs dynamics to breathe and hit hard.',
    ],
    workflow: [
      'Start with the riff or chord progression. Play it until it feels like a song, not just a progression.',
      'Demo the structure with a rough recording — arrangement first, tones second.',
      'Track drums, then bass, then rhythm guitars, then leads, then vocals. Build from the foundation up.',
      'Add production elements last: strings, keys, effects. Don\'t let production replace songwriting.',
      'Mix with the band — the live energy matters. Preserve the imperfections that make it human.',
    ],
  },
};

export function generateRecreationGuide(analysisResult, searchResult) {
  const genre = analysisResult.genre;
  const family = getGenreFamily(genre);
  const guide = GENRE_PRODUCTION_GUIDES[family] || GENRE_PRODUCTION_GUIDES['electronic'];

  // Build section-specific tips
  const sectionTips = analysisResult.sections.map(s => {
    const tips = [];
    if (s.energy < 0.3) tips.push(`Low energy section — keep it minimal. Focus on atmosphere and space.`);
    else if (s.energy >= 0.8) tips.push(`Peak energy — this should be the loudest, widest, most impactful moment.`);
    else tips.push(`Medium energy — building or transitioning. Layer elements incrementally.`);

    if (s.type === 'drop' || s.type === 'first-drop' || s.type === 'second-drop') {
      tips.push(`This is the main event. Every production decision should serve this moment.`);
    } else if (s.type === 'buildup') {
      tips.push(`Create anticipation with risers, filter sweeps, and rhythmic intensification.`);
    } else if (s.type === 'breakdown') {
      tips.push(`Strip it back. Contrast with the surrounding sections makes the drop hit harder.`);
    } else if (s.type === 'chorus') {
      tips.push(`The hook lives here. Make it wider, brighter, and more layered than the verse.`);
    } else if (s.type === 'verse') {
      tips.push(`Support the vocal/melody without competing. Leave space in the mid-range.`);
    } else if (s.type === 'intro') {
      tips.push(`Set the mood and establish the key musical elements. Don't reveal everything yet.`);
    } else if (s.type === 'outro') {
      tips.push(`Bring it home. Mirror the intro or gradually strip elements for a clean exit.`);
    }
    return { label: s.label, bars: s.bars, energy: s.energy, tips };
  });

  return {
    trackTitle: searchResult.title,
    trackArtist: searchResult.artist,
    genre,
    bpm: analysisResult.bpm,
    family,
    soundDesign: guide.soundDesign,
    arrangement: guide.arrangement,
    mixTechniques: guide.mixTechniques,
    workflow: guide.workflow,
    sectionTips,
    curatedInsights: analysisResult.referenceTrack?.insights || null,
  };
}

function getGenreFamily(genre) {
  const map = {
    'Drum & Bass': 'electronic', 'Liquid DnB': 'electronic', 'Neurofunk': 'electronic', 'Jungle': 'electronic',
    'Bass House': 'electronic', 'Deep House': 'electronic', 'Tech House': 'electronic', 'House': 'electronic',
    'Techno': 'electronic', 'Melodic Techno': 'electronic', 'Trance': 'electronic',
    'Dubstep': 'electronic', 'Riddim': 'electronic', 'Hardstyle': 'electronic',
    'Trap': 'hip-hop', 'Hip Hop': 'hip-hop', 'Boom Bap': 'hip-hop', 'Lo-Fi Hip Hop': 'hip-hop', 'Drill': 'hip-hop',
    'Pop': 'pop', 'R&B': 'pop', 'Dance Pop': 'pop',
    'Rock': 'rock', 'Punk Rock': 'rock', 'Metal': 'rock', 'Melodic Hardcore': 'rock',
  };
  return map[genre] || 'electronic';
}
